"use client";

import type { FeedRecordDetails } from "@mlx/contracts";

import { PersonName } from "@/components/app/common/person-name";
import { formatFieldDate, parseFieldDate } from "@/lib/format";
import { markdownToPlainText } from "@/lib/markdown-plain-text";
import type {
  CurrentMlxUser,
  WorkspaceOrganizationMember,
} from "@/lib/mlx-workspace-client";
import {
  currentMlxUserMatchesActor,
  organizationMemberForActor,
  workspaceUserDisplay,
  type WorkspaceSessionOwnerDisplay,
} from "@/lib/workspace-session-owner";

export type FeedRecordOperation = "created" | "updated" | "archived";

type RecordChange = FeedRecordDetails["changes"][number];

/* A value longer than this reads as prose, not as a before/after pair. The
   row then names the field and says it changed instead of quoting two stubs
   that look identical once truncated. */
const LONG_VALUE_LENGTH = 40;

export function FeedRecordChanges({
  details,
  operation = "updated",
  currentUser,
  membersByUserId,
}: {
  details: FeedRecordDetails;
  operation?: FeedRecordOperation;
  currentUser: CurrentMlxUser | null;
  membersByUserId: Map<string, WorkspaceOrganizationMember>;
}) {
  // A created record's fields are its content, not a change worth listing.
  if (operation === "created") return null;
  return (
    <>
      {details.changes.map((change, index) => (
        <span key={change.fieldKey}>
          {index > 0 ? ", " : ""}
          <FeedRecordChange
            change={change}
            currentUser={currentUser}
            membersByUserId={membersByUserId}
          />
        </span>
      ))}
      {details.hiddenChangeCount > 0
        ? ` (+${details.hiddenChangeCount} more)`
        : null}
    </>
  );
}

/** The same changes as plain text, for dense rows and tooltips. */
export function feedRecordChangesText(
  details: FeedRecordDetails,
  operation: FeedRecordOperation,
  currentUser: CurrentMlxUser | null,
  membersByUserId: Map<string, WorkspaceOrganizationMember>,
): string {
  if (operation === "created" || details.changes.length === 0) return "";
  const show = (value: unknown, fieldType: RecordChange["fieldType"]) =>
    valueText(value, fieldType, currentUser, membersByUserId);
  const parts = details.changes.map((change) => {
    const long =
      isLongValue(lengthValue(change.previousValue, change.fieldType)) ||
      isLongValue(lengthValue(change.value, change.fieldType));
    if (long) {
      const verb =
        change.changeKind === "removed"
          ? "cleared"
          : change.changeKind === "added"
            ? "added"
            : "updated";
      return `${change.label} ${verb}`;
    }
    if (change.changeKind === "removed")
      return `${change.label} removed (was ${show(change.previousValue, change.fieldType)})`;
    if (change.changeKind === "updated")
      return `${change.label}: ${show(change.previousValue, change.fieldType)} → ${show(change.value, change.fieldType)}`;
    return `${change.label} → ${show(change.value, change.fieldType)}`;
  });
  const hidden =
    details.hiddenChangeCount > 0
      ? ` (+${details.hiddenChangeCount} more)`
      : "";
  return `${parts.join(", ")}${hidden}`;
}

function valueText(
  value: unknown,
  fieldType: RecordChange["fieldType"],
  currentUser: CurrentMlxUser | null,
  membersByUserId: Map<string, WorkspaceOrganizationMember>,
): string {
  if (fieldType === "user" && typeof value === "string")
    return personDisplay(value, currentUser, membersByUserId).label;
  if (fieldType === "multiuser" && Array.isArray(value))
    return value
      .filter((entry): entry is string => typeof entry === "string")
      .map((entry) => personDisplay(entry, currentUser, membersByUserId).label)
      .join(", ");
  if (fieldType === "date" || fieldType === "datetime") {
    const formatted = formatFieldDate(value, fieldType === "datetime");
    if (formatted) return formatted;
  }
  return recordValueLabel(textValue(value, fieldType) ?? value);
}

function FeedRecordChange({
  change,
  currentUser,
  membersByUserId,
}: {
  change: RecordChange;
  currentUser: CurrentMlxUser | null;
  membersByUserId: Map<string, WorkspaceOrganizationMember>;
}) {
  const long =
    isLongValue(lengthValue(change.previousValue, change.fieldType)) ||
    isLongValue(lengthValue(change.value, change.fieldType));
  if (long) {
    const verb =
      change.changeKind === "removed"
        ? "cleared"
        : change.changeKind === "added"
          ? "added"
          : "updated";
    return (
      <>
        <span>{change.label}</span>
        {` ${verb}`}
      </>
    );
  }
  if (change.changeKind === "removed") {
    return (
      <>
        <span>{change.label}</span>
        {" removed"}
        <span className="text-tertiary-foreground">{" (was "}</span>
        <RecordChangeValue
          value={change.previousValue}
          fieldType={change.fieldType}
          currentUser={currentUser}
          membersByUserId={membersByUserId}
        />
        <span className="text-tertiary-foreground">{")"}</span>
      </>
    );
  }
  if (change.changeKind === "updated") {
    return (
      <>
        <span>{change.label}</span>
        {": "}
        <RecordChangeValue
          value={change.previousValue}
          fieldType={change.fieldType}
          currentUser={currentUser}
          membersByUserId={membersByUserId}
        />
        {" → "}
        <RecordChangeValue
          value={change.value}
          fieldType={change.fieldType}
          currentUser={currentUser}
          membersByUserId={membersByUserId}
        />
      </>
    );
  }
  return (
    <>
      <span>{change.label}</span>
      {" → "}
      <RecordChangeValue
        value={change.value}
        fieldType={change.fieldType}
        currentUser={currentUser}
        membersByUserId={membersByUserId}
      />
    </>
  );
}

function RecordChangeValue({
  value,
  fieldType,
  currentUser,
  membersByUserId,
}: {
  value: unknown;
  fieldType: RecordChange["fieldType"];
  currentUser: CurrentMlxUser | null;
  membersByUserId: Map<string, WorkspaceOrganizationMember>;
}) {
  if (fieldType === "user" && typeof value === "string") {
    return (
      <PersonName
        person={personDisplay(value, currentUser, membersByUserId)}
        variant="mention"
      />
    );
  }
  if (fieldType === "multiuser" && Array.isArray(value)) {
    return (
      <span className="inline-flex flex-wrap gap-x-1">
        {value
          .filter((entry): entry is string => typeof entry === "string")
          .map((entry, index) => (
            <span key={entry} className="inline-flex">
              {index > 0 ? ", " : ""}
              <PersonName
                person={personDisplay(entry, currentUser, membersByUserId)}
                variant="mention"
              />
            </span>
          ))}
      </span>
    );
  }
  if (fieldType === "date" || fieldType === "datetime") {
    const parsed = parseFieldDate(value);
    const formatted = formatFieldDate(value, fieldType === "datetime");
    if (parsed && formatted) {
      return (
        <time
          className="font-medium text-foreground"
          dateTime={
            fieldType === "date" && typeof value === "string"
              ? value
              : parsed.toISOString()
          }
          title={typeof value === "string" ? value : parsed.toISOString()}
        >
          {formatted}
        </time>
      );
    }
  }
  return (
    <span className="font-medium text-foreground">
      {recordValueLabel(textValue(value, fieldType) ?? value)}
    </span>
  );
}

// A Markdown document heading is content, not a typed channel reference.
// Leave ordinary text (including identifiers with underscores) untouched.
function textValue(
  value: unknown,
  fieldType: RecordChange["fieldType"],
): string | null {
  if (typeof value !== "string") return null;
  return fieldType === "text" && /^#{1,6}\s/m.test(value)
    ? markdownToPlainText(value).replace(/\s+/g, " ")
    : value;
}

function lengthValue(
  value: unknown,
  fieldType: RecordChange["fieldType"],
): string | null {
  if (fieldType === "date" || fieldType === "datetime")
    return formatFieldDate(value, fieldType === "datetime");
  return textValue(value, fieldType);
}

function isLongValue(value: string | null): boolean {
  return value !== null && value.length > LONG_VALUE_LENGTH;
}

function recordValueLabel(value: unknown): string {
  if (typeof value === "string")
    return value.length <= LONG_VALUE_LENGTH
      ? value
      : `${value.slice(0, LONG_VALUE_LENGTH - 1)}…`;
  if (typeof value === "number") return String(value);
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) {
    const visible = value.slice(0, 3).map(recordValueLabel).join(", ");
    return value.length > 3
      ? `${visible} (+${value.length - 3} more)`
      : visible;
  }
  return "Updated";
}

function personDisplay(
  userId: string,
  currentUser: CurrentMlxUser | null,
  membersByUserId: Map<string, WorkspaceOrganizationMember>,
): WorkspaceSessionOwnerDisplay {
  const known =
    currentMlxUserMatchesActor(currentUser, userId) ||
    Boolean(organizationMemberForActor(userId, membersByUserId));
  if (known) return workspaceUserDisplay(userId, currentUser, membersByUserId)!;
  return {
    label: "Unknown person",
    title: "Unknown person",
    ownerId: null,
    imageUrl: null,
    initials: "?",
  };
}
